import { useMemo } from 'react';

import config from 'config';

import featureNames from '~/shared/constants/featureFlags';
import { buildMasterProductGroupCodeObjects } from '~/shared/utils/buildMasterProductGroupCodeObjects';
import useHasFeature from '~/shared/utils/useHasFeature';

import { mergeUnifiedPrimaryProductsAndRelatedData } from '../transformers/mergeUnifiedPrimaryProductsAndRelatedData';
import { NormalizedCioResponseInfiniteData } from '../transformers/normalizeCioResponse';
import { CIOBrowseProductGridResponse } from '../types/gridResponses';

import useBatchedGqlProductPricing from './useBatchedGqlProductPricing';
import useBatchedGqlRelatedProducts from './useBatchedGqlRelatedProducts';
import useCioBrowseFacets from './useCioBrowseFacets';
import useCioBrowseProducts from './useCioBrowseProducts';
import useFacetTracking from './useFacetTracking';

type CIOBrowseProductsResponse = {
  data: NormalizedCioResponseInfiniteData<any>;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  cioFilterName: string | null;
  cioFilterValue: string | null;
  selectedSortOption: string | null;
  customFacetFilters: Record<string, any>;
  setCustomFacetFilters: (filters: Record<string, any>) => void;
};

const facetBlacklist = config.get<string[]>(
  'public.brand.constructor.facetBlacklist'
);

const useCioBrowseAndGqlProducts = (): CIOBrowseProductGridResponse => {
  const { selectedUrlFilters, allRegisteredFacets, allRegisteredFacetNames } =
    useCioBrowseFacets();

  const { enabled: isProductsByGroupCodeQueryEnabled } = useHasFeature(
    featureNames.PRODUCTS_BY_GROUP_CODE_QUERY
  );

  const {
    data: cioBrowseResults,
    isLoading: isPrimaryProductsLoading,
    isFetching: isPrimaryProductsFetching,
    // error,
    fetchNextPage,
    hasNextPage,
    cioFilterName,
    cioFilterValue,
    selectedSortOption,
    customFacetFilters,
    setCustomFacetFilters,
  } = useCioBrowseProducts({
    selectedUrlFilters,
    allRegisteredFacets,
  }) as CIOBrowseProductsResponse;

  const masterProductIds = useMemo(() => {
    return cioBrowseResults?.products?.map((result) => result?.masterProductId);
  }, [cioBrowseResults]);

  // dedupe by groupCode, merging masterProductIds
  const masterProductGroupCodeObjects = useMemo(
    () => buildMasterProductGroupCodeObjects(cioBrowseResults?.products ?? []),
    [cioBrowseResults]
  );

  const {
    data: pricingData,
    isLoading: isProductPricingLoading,
    isFetching: isProductPricingFetching,
  } = useBatchedGqlProductPricing(masterProductIds);
  const {
    data: relatedProductsData,
    isLoading: isRelatedProductsLoading,
    isFetching: isRelatedProductsFetching,
  } = useBatchedGqlRelatedProducts({
    masterProductIds,
    masterProductGroupCodeObjects,
  });

  // Combine the primary products with the pricingData and relatedProductsData
  const mergedProducts = useMemo(
    () =>
      mergeUnifiedPrimaryProductsAndRelatedData({
        primaryProducts: cioBrowseResults?.products,
        pricingData,
        relatedProductsData,
        isProductsByGroupCodeQueryEnabled,
      }),
    [
      cioBrowseResults,
      pricingData,
      relatedProductsData,
      isProductsByGroupCodeQueryEnabled,
    ]
  );

  const totalNumResults = cioBrowseResults?.pages?.[0]?.totalNumResults;

  const facets = useMemo(() => {
    const rawFacets = cioBrowseResults?.pages?.[0]?.response?.facets;
    return rawFacets?.filter((facet) => !facetBlacklist.includes(facet.name));
  }, [cioBrowseResults]);

  const sortOptions = useMemo(() => {
    return cioBrowseResults?.pages?.[0]?.response?.sort_options;
  }, [cioBrowseResults]);

  useFacetTracking({ selectedUrlFilters, selectedSortOption });

  return {
    products: mergedProducts,
    fetchNextPage,
    hasNextPage,
    facets,
    sortOptions,
    selectedUrlFilters,
    allRegisteredFacets,
    allRegisteredFacetNames,
    totalNumResults,
    isPrimaryProductsLoading,
    isPrimaryProductsFetching,
    isRelatedProductsLoading,
    isRelatedProductsFetching,
    isProductPricingLoading,
    isProductPricingFetching,
    cioFilterName,
    cioFilterValue,
    customFacetFilters,
    setCustomFacetFilters,
  };
};

export default useCioBrowseAndGqlProducts;
